import React, { useEffect, useState } from 'react'
import { useParams, Link, Navigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { PortableText } from '@portabletext/react'
import { client, urlFor } from '../lib/sanityClient'

const VIDEO_QUERY = `*[_type == "howToVideo" && slug.current == $slug && active == true][0] {
  _id,
  title,
  "slug": slug.current,
  excerpt,
  thumbnail,
  "videoUrl": videoFile.asset->url,
  youtubeUrl,
  category,
  difficulty,
  tags,
  durationSeconds,
  presenter,
  publishedAt,
  steps[] {
    stepNumber,
    heading,
    body,
    timestampSeconds
  },
  seoTitle,
  seoDescription
}`

const CATEGORY_LABELS = {
  'getting-started': 'Getting Started',
  'booking-scheduling': 'Booking & Scheduling',
  'payments-invoicing': 'Payments & Invoicing',
  'account-settings': 'Account & Settings',
  'jobs-quotes': 'Jobs & Quotes',
  'client-management': 'Client Management',
  'reporting-analytics': 'Reporting & Analytics',
  integrations: 'Integrations',
  troubleshooting: 'Troubleshooting',
}

const DIFFICULTY_CONFIG = {
  beginner: { label: 'Beginner', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  intermediate: { label: 'Intermediate', className: 'bg-amber-50 text-amber-700 border-amber-200' },
  advanced: { label: 'Advanced', className: 'bg-red-50 text-red-700 border-red-200' },
}

function formatDuration(seconds) {
  if (!seconds) return null
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return s > 0 ? `${m}m ${s}s` : `${m}m`
}

function formatDate(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

function formatTimestamp(seconds) {
  if (seconds == null) return null
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

function getYouTubeEmbedUrl(url) {
  if (!url) return null
  const fixedPlaylistId = 'PLLv_TrgJNdbCRKmRZKpTOPmV20XnB80ui'

  try {
    let videoId = null

    if (url.includes('youtu.be/')) {
      const u = new URL(url)
      videoId = u.pathname.split('/').filter(Boolean)[0] || null
    } else {
      const u = new URL(url)
      videoId = u.searchParams.get('v')
    }

    if (!videoId) return null

    const params = new URLSearchParams({
      rel: '0',
      listType: 'playlist',
      list: fixedPlaylistId,
      modestbranding: '1',
    })

    return `https://www.youtube.com/embed/${videoId}?${params.toString()}`
  } catch {
    return null
  }
}

const portableComponents = {
  block: {
    normal: ({ children }) => (
      <p className="text-secondary leading-relaxed mb-4 text-sm md:text-base">{children}</p>
    ),
  },
  marks: {
    strong: ({ children }) => <strong className="font-semibold text-primary">{children}</strong>,
    em: ({ children }) => <em className="italic">{children}</em>,
    code: ({ children }) => (
      <code className="bg-surface-2 text-accent font-mono text-xs px-1.5 py-0.5 rounded">{children}</code>
    ),
  },
  types: {
    image: ({ value }) => {
      const src = urlFor(value).width(800).auto('format').url()
      return (
        <figure className="my-5">
          <img
            src={src}
            alt={value.alt || ''}
            className="w-full rounded-xl border border-border-subtle"
          />
          {value.caption && (
            <figcaption className="text-center text-xs text-secondary mt-2">{value.caption}</figcaption>
          )}
        </figure>
      )
    },
  },
}

export default function HowToVideoPost() {
  const { slug } = useParams()
  const [video, setVideo] = useState(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)

  useEffect(() => {
    if (!slug) return
    setLoading(true)
    setNotFound(false)

    client
      .fetch(VIDEO_QUERY, { slug })
      .then((data) => {
        if (data) {
          setVideo(data)
        } else {
          setNotFound(true)
        }
        setLoading(false)
      })
      .catch(() => {
        setNotFound(true)
        setLoading(false)
      })
  }, [slug])

  if (notFound) return <Navigate to="/how-to-videos" replace />

  if (loading) {
    return (
      <main className="min-h-screen bg-surface-0 pt-24">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-16 animate-pulse space-y-6">
          <div className="h-4 bg-surface-2 rounded w-1/5" />
          <div className="h-10 bg-surface-2 rounded w-5/6" />
          <div className="h-10 bg-surface-2 rounded w-3/4" />
          <div className="aspect-video bg-surface-2 rounded-2xl" />
          <div className="space-y-3 pt-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className={`h-4 bg-surface-2 rounded ${i % 3 === 2 ? 'w-3/4' : 'w-full'}`} />
            ))}
          </div>
        </div>
      </main>
    )
  }

  if (!video) return null

  const embedUrl = getYouTubeEmbedUrl(video.youtubeUrl)
  const difficulty = DIFFICULTY_CONFIG[video.difficulty]
  const duration = formatDuration(video.durationSeconds)
  const sortedSteps = video.steps
    ? [...video.steps].sort((a, b) => (a.stepNumber ?? 0) - (b.stepNumber ?? 0))
    : []

  return (
    <main className="min-h-screen bg-surface-0">
      {/* Back nav */}
      <div className="pt-24 pb-0 px-4 md:px-8 lg:px-16">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Link
              to="/how-to-videos"
              className="inline-flex items-center gap-2 text-sm text-secondary hover:text-primary transition-colors duration-200 cursor-pointer"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M11 17l-5-5 5-5M6 12h12" />
              </svg>
              All guides
            </Link>
          </motion.div>
        </div>
      </div>

      <article className="px-4 md:px-8 lg:px-16 pt-8 pb-20">
        <div className="max-w-4xl mx-auto">

          {/* Header */}
          <motion.header
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-8"
          >
            {/* Meta row */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
              {video.category && (
                <span className="badge bg-accent/10 text-accent text-xs">
                  {CATEGORY_LABELS[video.category] || video.category}
                </span>
              )}
              {difficulty && (
                <span className={`inline-flex items-center text-xs font-medium px-2.5 py-0.5 rounded-full border ${difficulty.className}`}>
                  {difficulty.label}
                </span>
              )}
              {duration && (
                <span className="inline-flex items-center gap-1 text-xs text-secondary">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {duration}
                </span>
              )}
              <time className="text-xs text-secondary" dateTime={video.publishedAt}>
                {formatDate(video.publishedAt)}
              </time>
            </div>

            {/* Title */}
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-primary leading-tight mb-4">
              {video.title}
            </h1>

            {/* Excerpt */}
            {video.excerpt && (
              <p className="text-lg text-secondary leading-relaxed border-l-4 border-accent pl-4">
                {video.excerpt}
              </p>
            )}

            {/* Presenter */}
            {video.presenter && (
              <div className="flex items-center gap-3 mt-5 pt-5 border-t border-border-subtle">
                <div className="w-10 h-10 rounded-full bg-accent/15 flex items-center justify-center flex-shrink-0">
                  <span className="text-accent font-semibold">
                    {video.presenter.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-primary">{video.presenter}</p>
                  <p className="text-xs text-secondary">Presenter</p>
                </div>
              </div>
            )}
          </motion.header>

          {/* Video Player — only rendered when a video source is available */}
          {(embedUrl || video.videoUrl) && (
            <motion.div
              initial={{ opacity: 0, scale: 0.99 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="mb-10"
            >
              {embedUrl ? (
                /* YouTube embed */
                <div className="relative w-full aspect-video rounded-2xl overflow-hidden border border-border-subtle shadow-card bg-black">
                  <iframe
                    src={embedUrl}
                    title={video.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    className="absolute inset-0 w-full h-full"
                  />
                </div>
              ) : (
                /* Uploaded video file */
                <div className="relative w-full aspect-video rounded-2xl overflow-hidden border border-border-subtle shadow-card bg-black">
                  <video
                    src={video.videoUrl}
                    controls
                    className="absolute inset-0 w-full h-full"
                    poster={
                      video.thumbnail
                        ? urlFor(video.thumbnail).width(1280).height(720).auto('format').fit('crop').url()
                        : undefined
                    }
                  >
                    Your browser does not support the video tag.
                  </video>
                </div>
              )}
            </motion.div>
          )}

          {/* Tags */}
          {video.tags && video.tags.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.2 }}
              className="flex flex-wrap gap-2 mb-10"
            >
              {video.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/how-to-videos?tag=${encodeURIComponent(tag)}`}
                  className="text-sm text-secondary bg-surface-2 hover:bg-accent/10 hover:text-accent px-3 py-1 rounded-full border border-border-subtle hover:border-accent transition-all duration-200 cursor-pointer"
                >
                  {tag}
                </Link>
              ))}
            </motion.div>
          )}

          {/* Written Steps */}
          {sortedSteps.length > 0 && (
            <motion.section
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.25 }}
              className="mb-14"
            >
              <h2 className="text-xl font-bold text-primary mb-4">Step-by-Step Guide</h2>
              <div className="space-y-2">
                {sortedSteps.map((step) => (
                  <div
                    key={step.stepNumber}
                    className="flex gap-4 rounded-xl border border-border-subtle bg-white px-4 py-3"
                  >
                    {/* Step number */}
                    <div className="w-7 h-7 rounded-lg bg-accent text-white flex items-center justify-center font-bold text-xs flex-shrink-0 mt-0.5">
                      {step.stepNumber}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-0.5">
                        <h3 className="text-sm font-semibold text-primary">{step.heading}</h3>
                        {step.timestampSeconds != null && (
                          <span className="inline-flex items-center gap-1 text-xs text-accent bg-accent/10 px-1.5 py-0.5 rounded-full font-medium">
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {formatTimestamp(step.timestampSeconds)}
                          </span>
                        )}
                      </div>
                      {step.body && (
                        <div className="prose-sm">
                          <PortableText value={step.body} components={portableComponents} />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </motion.section>
          )}

          {/* Footer CTA */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="mt-16 pt-10 border-t border-border-subtle flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
          >
            <div>
              <p className="text-sm font-semibold text-primary">Need more help?</p>
              <p className="text-sm text-secondary">Browse more guides or contact our support team.</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to="/how-to-videos" className="btn-ghost text-sm py-2.5 px-5">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11 17l-5-5 5-5M6 12h12" />
                </svg>
                All guides
              </Link>
              <Link to="/support" className="btn-primary text-sm py-2.5 px-5">
                Get support
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5-5 5M6 12h12" />
                </svg>
              </Link>
            </div>
          </motion.div>

        </div>
      </article>
    </main>
  )
}
